import { useState, useEffect, memo, useCallback, type CSSProperties } from 'react'
import { antennaElementTypesApi } from '../../lib/api.js'
import type { AntennaElementType } from '../../types/domain.js'
import { AntennaElementTypeModal } from './AntennaElementTypeModal.js'
import { Spinner } from '../common/Spinner.js'
import '../programs/ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface AntennaElementTypesPanelProps {
  channelId: string
  canManage?: boolean
  /** Rejoué après chaque écriture : le filtre du catalogue lit la même liste. */
  onChanged?: () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const AntennaElementTypesPanel = memo(function AntennaElementTypesPanel({ channelId, canManage = true, onChanged }: AntennaElementTypesPanelProps) {
  const [types,     setTypes]     = useState<AntennaElementType[]>([])
  const [loading,   setLoading]   = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing,   setEditing]   = useState<AntennaElementType | undefined>(undefined)
  const [search,    setSearch]    = useState('')
  const [sortOrder, setSortOrder] = useState<'manual' | 'name-asc' | 'name-desc' | 'count-desc'>('manual')

  const reload = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    antennaElementTypesApi.list({ channelId })
      .then(setTypes)
      .finally(() => setLoading(false))
  }, [channelId])

  useEffect(() => { reload() }, [reload])

  const handleCreated = useCallback((elementType: AntennaElementType) => {
    setTypes(prev => [...prev, elementType].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr')))
    setShowModal(false)
    onChanged?.()
  }, [onChanged])

  const handleUpdated = useCallback((elementType: AntennaElementType) => {
    setTypes(prev => prev.map(t => t.id === elementType.id ? elementType : t))
    setEditing(undefined)
    setShowModal(false)
    onChanged?.()
  }, [onChanged])

  const handleDeleted = useCallback((id: string) => {
    setTypes(prev => prev.filter(t => t.id !== id))
    onChanged?.()
  }, [onChanged])

  const handleEdit = useCallback((elementType: AntennaElementType, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(elementType)
    setShowModal(true)
  }, [])

  const handleNew = useCallback(() => {
    setEditing(undefined)
    setShowModal(true)
  }, [])

  const handleCloseModal = useCallback(() => { setShowModal(false); setEditing(undefined) }, [])

  const filtered = types.filter(t =>
    !search || t.label.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (sortOrder === 'name-asc') return a.label.localeCompare(b.label, 'fr')
    if (sortOrder === 'name-desc') return b.label.localeCompare(a.label, 'fr')
    if (sortOrder === 'count-desc') return (b._count?.programs ?? 0) - (a._count?.programs ?? 0)
    return a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr')
  })

  return (
    <div className="catalog-list">

      {/* Toolbar */}
      <div className="catalog-toolbar catalog-toolbar--stack">
        <div className="catalog-search-row">
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="tp-input catalog-search"
            aria-label="Rechercher un type d’élément d’antenne"
          />
          <button
            type="button"
            onClick={handleNew}
            disabled={!canManage}
            title={!canManage ? 'Permission de gestion des éléments d’antenne requise' : undefined}
            className="tp-button tp-button--info"
          >
            + Nouveau
          </button>
        </div>

        <div className="catalog-filter-dropdowns">
          <select
            className="tp-select catalog-filter-select"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value as typeof sortOrder)}
            aria-label="Trier les types d’élément d’antenne"
          >
            <option value="manual">Ordre de tri manuel</option>
            <option value="name-asc">Nom (A-Z)</option>
            <option value="name-desc">Nom (Z-A)</option>
            <option value="count-desc">Nombre d’éléments (décroissant)</option>
          </select>
        </div>
      </div>

      {/* Compteur */}
      <div className="catalog-count" role="status">
        {loading ? <><Spinner /> Chargement…</> : `${filtered.length} type${filtered.length > 1 ? 's' : ''} d’élément d’antenne`}
      </div>

      {/* Liste */}
      <div className="catalog-scroll">
        {loading ? null : filtered.length === 0 ? (
          <div className="catalog-empty">
            {types.length === 0
              ? 'Aucun type d’élément d’antenne. Créez-en un pour classer votre habillage (Habillage météo, Générique de fin…).'
              : 'Aucun résultat'}
          </div>
        ) : (
          filtered.map(elementType => (
            <AntennaElementTypeRow
              key={elementType.id}
              elementType={elementType}
              onEdit={(e) => handleEdit(elementType, e)}
              onDeleted={() => handleDeleted(elementType.id)}
              canManage={canManage}
            />
          ))
        )}
      </div>

      {showModal && canManage && (
        <AntennaElementTypeModal
          channelId={channelId}
          elementType={editing}
          onCreated={handleCreated}
          onUpdated={handleUpdated}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
})

// ─── Ligne ────────────────────────────────────────────────────────────────────

const AntennaElementTypeRow = memo(function AntennaElementTypeRow({ elementType, onEdit, onDeleted, canManage }: {
  elementType: AntennaElementType
  onEdit:      (e: React.MouseEvent) => void
  onDeleted:   () => void
  canManage:   boolean
}) {
  const accentStyle = elementType.color
    ? { '--catalog-accent': elementType.color } as CSSProperties
    : undefined
  const count = elementType._count?.programs ?? 0

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`Supprimer le type d’élément d’antenne "${elementType.label}" ?`)) return
    try {
      await antennaElementTypesApi.delete(elementType.id)
      onDeleted()
    } catch (err: any) {
      alert(err.message)
    }
  }

  return (
    <div className="catalog-collection-row">
      <span className="catalog-collection-row__select">
        <span className="catalog-collection-row__dot" style={accentStyle} aria-hidden="true" />
        <span className="catalog-collection-row__copy">
          <span className="catalog-collection-row__title">{elementType.label}</span>
          <span className="catalog-collection-row__meta">
            {count} élément{count !== 1 ? 's' : ''}
            {elementType.builtin ? ' · type d’origine' : ''}
          </span>
        </span>
      </span>

      <div className="catalog-row-actions">
        <button
          type="button"
          onClick={onEdit}
          disabled={!canManage}
          title={canManage ? 'Renommer' : 'Permission de gestion des éléments d’antenne requise'}
          aria-label={`Modifier ${elementType.label}`}
          className="catalog-icon-action"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!canManage || elementType.builtin || count > 0}
          title={
            elementType.builtin ? 'Un type d’origine ne peut pas être supprimé'
              : count > 0 ? 'Ce type est encore porté par des éléments d’antenne'
                : canManage ? 'Supprimer' : 'Permission de gestion des éléments d’antenne requise'
          }
          aria-label={`Supprimer ${elementType.label}`}
          className="catalog-icon-action catalog-icon-action--danger"
        >
          ✕
        </button>
      </div>
    </div>
  )
})
